import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  CardActions,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import LiveKitOutboundTester from './LiveKitOutboundTester';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallIcon from '@mui/icons-material/Call';
import EventIcon from '@mui/icons-material/Event';
import CreateAgent from './CreateAgent';
import FunctionsList from './FunctionsList';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [editingAgent, setEditingAgent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAgency = user?.role === 'agency';
  const isClient = user?.role === 'client';
  const canManageAgents = isAdmin || isAgency || isClient;
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentCalls, setAgentCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callsDialog, setCallsDialog] = useState(false);
  const navigate = useNavigate();
  const [testAgentDialogOpen, setTestAgentDialogOpen] = useState(false);
  const [selectedTestAgent, setSelectedTestAgent] = useState(null);
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for Agents, 1 for Functions
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billableUsers, setBillableUsers] = useState([]);
  const [selectedBillableUserId, setSelectedBillableUserId] = useState('');
  const [loadingBillableUsers, setLoadingBillableUsers] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Client config dialog state
  const [clientConfigDialogOpen, setClientConfigDialogOpen] = useState(false);
  const [clientConfigurable, setClientConfigurable] = useState(false);
  const [savingClientConfig, setSavingClientConfig] = useState(false);
  
  // Calendar functionality
  const [enableCalendar, setEnableCalendar] = useState(false);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [calendarError, setCalendarError] = useState('');

  // First add a new state variable for client configuration permission
  const [allowClientConfig, setAllowClientConfig] = useState(false);

  // Add theme and responsive states
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    fetchAgents();
  }, []);

  // Add a useEffect to log agent data after state updates
  useEffect(() => {
    if (agents.length > 0) {
      console.log('Current agents state after processing:');
      agents.forEach(agent => {
        console.log(`Agent: ${agent.name}, External: ${agent.is_external ? 'Yes' : 'No'}`);
      });
    }
  }, [agents]);

  // Function to determine if edit button should be shown
  const shouldShowEditButton = (agent) => {
    console.log(`Checking edit permission for ${agent.name}, is_external=${agent.is_external}`);
    
    // External agents cannot be edited by anyone
    if (agent.is_external) {
      console.log(`${agent.name} is external, hiding edit button regardless of permissions`);
      return false;
    }
    
    // Admin can edit any internal agent
    if (isAdmin) {
      console.log(`User is admin, allowing edit for internal agent ${agent.name}`);
      return true;
    }
    
    // Agency users can edit if they own the agent
    if (isAgency && user?.id === agent.owner_id) {
      console.log(`Agency user is owner of ${agent.name}, allowing edit`);
      return true;
    }
    
    // For all users: check if agent is client_configurable
    // The agent.client_configurable property determines if the agent can be configured by clients
    const hasPermission = agent.client_configurable;
    console.log(`${agent.name} is internal, client_configurable: ${hasPermission}`);
    return hasPermission;
  };

  const fetchAgents = async (retryCount = 0) => {
    setLoading(true);
    try {
      console.log(`Fetching agents... (attempt ${retryCount + 1})`);
      
      // Fetch both regular agents and Plug and Play agents
      const [regularResponse, plugnplayResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/plugnplay/agents`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        })
      ]);
      
      // Handle rate limiting (429) with exponential backoff
      if (regularResponse.status === 429 || plugnplayResponse.status === 429) {
        const waitTime = Math.min(1000 * (2 ** retryCount), 10000); // Exponential backoff with 10s max
        console.log(`Rate limited, retrying in ${waitTime}ms...`);
        
        if (retryCount < 3) { // Max 3 retries
          setTimeout(() => fetchAgents(retryCount + 1), waitTime);
          return;
        } else {
          throw new Error("Too many requests. Please try again later.");
        }
      }
      
      // Process regular agents
      let regularAgents = [];
      if (regularResponse.ok) {
        const regularData = await regularResponse.json();
        if (Array.isArray(regularData)) {
          regularAgents = regularData;
        } else if (regularData.agents && Array.isArray(regularData.agents)) {
          regularAgents = regularData.agents;
        }
      }
      
      // Process Plug and Play agents
      let plugnplayAgents = [];
      if (plugnplayResponse.ok) {
        const plugnplayData = await plugnplayResponse.json();
        if (Array.isArray(plugnplayData)) {
          plugnplayAgents = plugnplayData.map(agent => ({
            ...agent,
            is_plugnplay: true,
            agent_type: agent.agent_type || 'plugnplay'
          }));
        }
      }
      
      // Combine all agents
      const allAgents = [...regularAgents, ...plugnplayAgents];
      console.log('Combined agent data received:', allAgents);
      
             if (allAgents.length > 0) {
        // Process each agent
        allAgents.forEach(agent => {
          // Start with the proper determination based on the actual database values
          // For known external agents in the database
          if (agent.name === 'Vogelensangh' || 
              agent.name === 'Outbound Demo Solar' || 
              agent.name === 'Outbound Demo Installation' || 
              agent.name === 'Inbound Demo Installation') {
            // Force is_external to true in case it's missing or incorrect
            agent.is_external = true;
            console.log(`Forcing ${agent.name} to be external (true)`);
          } else if (agent.name === 'pilot' || agent.name === 'first-outbound' ||
                     agent.name === 'Tester' || agent.name === 'Updated Agent 1747376342') {
            // Force is_external to false for known internal agents
            agent.is_external = false;
            console.log(`Forcing ${agent.name} to be internal (false)`);
          }
          
          // Plug and Play agents are never external
          if (agent.is_plugnplay) {
            agent.is_external = false;
            console.log(`${agent.name} is Plug and Play agent, setting as internal`);
          }
          
          // Log the final determination
          console.log(`Agent: ${agent.name}, Type: ${agent.agent_type}, External: ${agent.is_external ? 'Yes' : 'No'}, PlugnPlay: ${agent.is_plugnplay ? 'Yes' : 'No'}`);
          });
        setAgents(allAgents);
        } else {
        // If no agents found, set empty array
        console.log('No agents found');
        setAgents([]);
      }
      // After fetching agents
      console.log('Full agent objects:', agents);
      agents.forEach(agent => {
        console.log(`Agent: ${agent.name}, Full data:`, agent);
      });
    } catch (error) {
      console.error('Error fetching agents:', error);
      showSnackbar('Error fetching agents', 'error');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillableUsers = async (agentParam) => {
    // Use the parameter if provided, otherwise fall back to state
    const agent = agentParam || selectedAgent;
    
    if (!agent) {
      console.error('Cannot fetch billable users: No agent selected');
      return;
    }
    
    console.log(`Fetching billable users for agent: ${agent.id} (${agent.name})`);
    setLoadingBillableUsers(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/billable-users?agent_id=${agent.id}`;
      console.log(`Requesting: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      console.log(`API Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Billable users response data:', data);
        
        // Filter users based on role if needed
        const users = Array.isArray(data) ? data : (data.users || []);
        console.log(`Found ${users.length} billable users`);
        setBillableUsers(users);
      } else {
        const errorText = await response.text();
        console.error(`Error fetching billable users: ${response.status}`, errorText);
        showSnackbar('Error fetching users', 'error');
        setBillableUsers([]);
      }
    } catch (error) {
      console.error('Error fetching billable users:', error);
      showSnackbar('Error fetching users', 'error');
      setBillableUsers([]);
    } finally {
      setLoadingBillableUsers(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = () => {
    setEditingAgent(null);
    setNewAgentName('');
    setEnableCalendar(false);
    setSelectedCalendarId('');
    setAllowClientConfig(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewAgentName('');
    setEditingAgent(null);
  };

  const handleOpenBillingDialog = (agent) => {
    console.log('Opening billing dialog for agent:', agent);
    setSelectedAgent(agent);
    setSelectedBillableUserId(agent.billable_user_id || '');
    setBillingDialogOpen(true);
    
    // Set timeout to ensure the dialog is open before fetching
    setTimeout(() => {
      console.log('Delayed fetchBillableUsers call to ensure dialog is open');
      fetchBillableUsers(agent);
    }, 100);
  };

  const handleCloseBillingDialog = () => {
    setBillingDialogOpen(false);
    setSelectedAgent(null);
    setSelectedBillableUserId('');
  };

  const handleOpenClientConfigDialog = (agent) => {
    setSelectedAgent(agent);
    setClientConfigurable(agent.client_configurable || false);
    setClientConfigDialogOpen(true);
  };

  const handleCloseClientConfigDialog = () => {
    setClientConfigDialogOpen(false);
    setSelectedAgent(null);
    setClientConfigurable(false);
  };

  const handleSaveClientConfigSettings = async () => {
    if (!selectedAgent) {
      console.error('No agent selected for saving client configuration settings');
      return;
    }
    
    setSavingClientConfig(true);
    
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/agents/${selectedAgent.id}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          client_configurable: clientConfigurable
        })
      });
      
      if (response.ok) {
        showSnackbar('Client configuration settings saved successfully', 'success');
        setClientConfigDialogOpen(false);
        
        // Update the agent in the state to reflect changes
        const updatedAgents = agents.map(agent => 
          agent.id === selectedAgent.id 
            ? { ...agent, client_configurable: clientConfigurable } 
            : agent
        );
        setAgents(updatedAgents);
      } else {
        const errorText = await response.text();
        showSnackbar(`Error: ${errorText || 'Failed to save client configuration settings'}`, 'error');
      }
    } catch (error) {
      console.error('Error updating client configuration settings:', error);
      showSnackbar('Error: Could not connect to server', 'error');
    } finally {
      setSavingClientConfig(false);
    }
  };

  const handleSaveBillingSettings = async () => {
    if (!selectedAgent) {
      console.error('No agent selected for saving billing settings');
      return;
    }
    
    console.log(`Saving billing settings for agent ${selectedAgent.id} with billable user ID: ${selectedBillableUserId}`);
    setSavingBilling(true);
    
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/agent-billing/${selectedAgent.id}`;
      console.log(`Sending PUT request to: ${url}`);
      console.log('Request payload:', { 
        billable_user_id: selectedBillableUserId || null
      });
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          billable_user_id: selectedBillableUserId || null
        })
      });
      
      console.log(`Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Billing settings saved successfully:', data);
        showSnackbar('Billing settings saved successfully', 'success');
        setBillingDialogOpen(false);
        
        // Update the agent in the state to reflect changes
        const updatedAgents = agents.map(agent => 
          agent.id === selectedAgent.id 
            ? { ...agent, billable_user_id: selectedBillableUserId } 
            : agent
        );
        setAgents(updatedAgents);
      } else {
        const errorText = await response.text();
        console.error(`Error saving billing settings: ${response.status}`, errorText);
        showSnackbar(`Error: ${errorText || 'Failed to save billing settings'}`, 'error');
      }
    } catch (error) {
      console.error('Error updating billing settings:', error);
      showSnackbar('Error: Could not connect to server', 'error');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleCreateAgent = () => {
    // Clear any previous agent data
    setNewAgentName('');
    setEnableCalendar(false);
    setSelectedCalendarId('');
    setAllowClientConfig(false);
    
    // Open the create agent dialog
    setCreateAgentDialogOpen(true);
  };

  const handleCloseCreateAgentDialog = () => {
    setCreateAgentDialogOpen(false);
  };

  const handleAgentCreated = (newAgent) => {
    // Add the newly created agent to the list
    if (newAgent) {
      // Set the owner_id to the current user's ID if it's not set
      if (!newAgent.owner_id && user) {
        newAgent.owner_id = user.id;
      }
      
      // Add the new agent to the state
      setAgents(prevAgents => [...prevAgents, newAgent]);
      showSnackbar('Agent created successfully', 'success');
    }
    handleCloseCreateAgentDialog();
  };

  const handleUpdateAgent = async () => {
    if (!newAgentName.trim()) {
      showSnackbar('Please enter an agent name', 'error');
      return;
    }

    setSaving(true);
    try {
      // Create the update data object with calendar information and client config permission
      const updateData = {
        name: newAgentName.trim(),
        has_calendar: enableCalendar,
        calendar_id: enableCalendar ? selectedCalendarId : null,
        client_configurable: allowClientConfig
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agents/${editingAgent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error('Failed to update agent');
      }

      // Update the agents list with the new information
      const updatedAgents = agents.map(agent => 
        agent.id === editingAgent.id 
          ? { 
              ...agent, 
              name: newAgentName.trim(),
              has_calendar: enableCalendar,
              calendar_id: enableCalendar ? selectedCalendarId : null,
              client_configurable: allowClientConfig
            } 
          : agent
      );
      setAgents(updatedAgents);
      
      setOpenDialog(false);
      showSnackbar('Agent updated successfully');
    } catch (error) {
      console.error('Error updating agent:', error);
      showSnackbar('Error updating agent', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAgent = (agentId, e) => {
    e.stopPropagation();
    
    // Find the agent to edit
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      // Check if agent is external and user is not admin/agency
      if (agent.is_external && !isAdmin && !isAgency) {
        showSnackbar('This is an external agent. Only administrators can edit its configuration.', 'error');
        return;
      }
      
      // Check if user can edit this agent
      if (!isAdmin && !isAgency && !agent.client_configurable && user?.id !== agent.owner_id) {
        showSnackbar('You do not have permission to edit this agent configuration', 'error');
        return;
      }
      
      // For Plug and Play agents, navigate to a special edit page
      if (agent.is_plugnplay) {
        navigate(`/plugnplay/edit/${agent.id}`);
      } else {
        // Navigate to the regular agent configuration page
      navigate(`/agents/edit/${agent.id}`);
      }
    } else {
      navigate(`/agents/edit/${agentId}`);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!isAdmin) return;
    
    if (!window.confirm('Are you sure you want to delete this agent?')) {
      return;
    }
    
    try {
      // Find the agent to determine its type
      const agent = agents.find(a => a.id === agentId);
      
      // Use the appropriate endpoint based on agent type
      let deleteUrl;
      if (agent && (agent.is_plugnplay || agent.agent_type === 'plugnplay')) {
        deleteUrl = `${process.env.REACT_APP_API_URL}/api/plugnplay/delete/${agentId}`;
      } else {
        deleteUrl = `${process.env.REACT_APP_API_URL}/api/agent-creation/delete/${agentId}`;
      }
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        showSnackbar('Agent deleted successfully');
        fetchAgents();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error deleting agent', 'error');
      }
    } catch (error) {
      showSnackbar('Error deleting agent', 'error');
    }
  };

  const handleViewCalls = async (agent) => {
    setSelectedAgent(agent);
    setLoadingCalls(true);
    setCallsDialog(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calls/agent/${agent.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgentCalls(Array.isArray(data) ? data : []);
      } else {
        showSnackbar('Error fetching calls', 'error');
        setAgentCalls([]);
      }
    } catch (error) {
      showSnackbar('Error fetching calls', 'error');
      setAgentCalls([]);
    } finally {
      setLoadingCalls(false);
    }
  };

  const handleCloseCallsDialog = () => {
    setCallsDialog(false);
    setSelectedAgent(null);
    setAgentCalls([]);
  };

  const handleTestAgent = (agent) => {
    // For plug-and-play agents, redirect to the edit page where the integrated playground is
    if (agent.is_plugnplay) {
      navigate(`/plugnplay/edit/${agent.id}`, { 
        state: { autoStartTest: true } 
      });
    } else {
      // For regular agents, also navigate to edit page with auto-start test
      navigate(`/agents/edit/${agent.id}`, { 
        state: { autoStartTest: true } 
      });
    }
  };

  const handleCloseTestDialog = () => {
    setTestAgentDialogOpen(false);
    setSelectedTestAgent(null);
  };

  const getBillableUserName = (agent) => {
    if (!agent.billable_user_id) return 'Not set';
    
    // Try to find the user in the billableUsers list
    const user = billableUsers.find(user => user.id === agent.billable_user_id);
    if (user) return user.email;
    
    // Fallback to the attribute directly from agent if available
    return agent.billable_user_email || 'Unknown user';
  };

  // Function to fetch available calendars
  const fetchAvailableCalendars = async () => {
    setLoadingCalendars(true);
    setCalendarError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/calendars`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.calendars && data.calendars.length > 0) {
          setAvailableCalendars(data.calendars);
          
          // If we're editing and no calendar is selected yet, select the first one
          if (editingAgent && editingAgent.has_calendar && !selectedCalendarId) {
            setSelectedCalendarId(data.calendars[0].id);
          }
        } else {
          setCalendarError('No calendars found. Please connect your Google Calendar first.');
        }
      } else if (response.status === 400) {
        setCalendarError('Calendar not connected. Please connect your Google Calendar first.');
      } else {
        const errorData = await response.json();
        setCalendarError(errorData.error || 'Failed to fetch calendars');
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setCalendarError('Error connecting to server');
    } finally {
      setLoadingCalendars(false);
    }
  };

  // Add this new method to handle updating the agent name
  const handleUpdateAgentName = async (agentId) => {
    if (!newAgentName.trim()) {
      showSnackbar('Agent name cannot be empty', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/agent/${agentId}/name`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newAgentName.trim() })
      });
      
      if (response.ok) {
        // Update agent in state
        const updatedAgents = agents.map(agent => 
          agent.id === agentId 
            ? { ...agent, name: newAgentName.trim() } 
            : agent
        );
        setAgents(updatedAgents);
        setEditingAgent(null);
        showSnackbar('Agent name updated successfully', 'success');
      } else {
        showSnackbar('Failed to update agent name', 'error');
      }
    } catch (error) {
      console.error('Error updating agent name:', error);
      showSnackbar('Failed to update agent name', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  } else if (agents.length === 0) {
    return (
      <Box sx={{ p: isMobile ? 1 : 3 }}>
        <Typography variant="h5" align="center">
          Error loading agents
        </Typography>
      </Box>
    );
  } else {
  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 3,
        gap: isMobile ? 2 : 0
      }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Agents</Typography>
        <Box>
          {canManageAgents && activeTab === 0 && (
            <>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleCreateAgent}
                size={isMobile ? "small" : "medium"}
                fullWidth={isMobile}
              >
                Add Agent
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(event, newValue) => setActiveTab(newValue)} 
        aria-label="basic tabs example"
        variant={isMobile ? "fullWidth" : "standard"}
      >
        <Tab label="Agents" />
        <Tab label="Functions" />
      </Tabs>

      {activeTab === 0 ? (
        // Agents Tab
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
          {!agents || agents.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    No agents available
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {canManageAgents 
                      ? 'Create your first agent to get started' 
                      : 'No agents have been assigned to you yet'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            agents.map((agent) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ flex: '1 0 auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {agent.agent_type === 'outbound' ? (
                          <PhoneIcon sx={{ color: 'primary.main', mr: 1 }} />
                        ) : (
                          <CallIcon sx={{ color: 'primary.main', mr: 1 }} />
                        )}
                          {editingAgent && editingAgent.id === agent.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <TextField
                                value={newAgentName}
                                onChange={(e) => setNewAgentName(e.target.value)}
                                size="small"
                                autoFocus
                                sx={{ flexGrow: 1 }}
                              />
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateAgentName(agent.id);
                                }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAgent(null);
                                  setNewAgentName('');
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography 
                              variant="h6" 
                              noWrap 
                              sx={{ 
                                maxWidth: isMobile ? '150px' : '200px',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                                  borderRadius: 1,
                                  px: 1
                                },
                                px: 1
                              }}
                              onClick={() => {
                                // Only allow name editing if admin/agency or internal agent
                                if (isAdmin || isAgency || !agent.is_external) {
                                  setEditingAgent(agent);
                                  setNewAgentName(agent.name);
                                } else {
                                  showSnackbar('External agents can only be edited by administrators', 'error');
                                }
                              }}
                            >
                          {agent.name}
                        </Typography>
                          )}
                      </Box>
                      <Box>
                        {agent.has_calendar && (
                          <Tooltip title="Calendar Functionality Enabled">
                            <EventIcon sx={{ color: 'primary.main', mr: 1 }} />
                          </Tooltip>
                        )}
                        {agent.client_configurable && (
                          <Tooltip title="Client Configuration Allowed">
                            <SettingsIcon sx={{ color: 'info.main', mr: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                      label={agent.agent_type === 'outbound' ? 'Outbound' : 
                            agent.agent_type === 'plugnplay' ? 'Plug & Play' : 'Inbound'} 
                        size="small" 
                      color={agent.agent_type === 'outbound' ? 'primary' : 
                            agent.agent_type === 'plugnplay' ? 'secondary' : 'success'}
                        sx={{ mr: 1, mb: isMobile ? 1 : 0 }}
                      />
                        {agent.is_external && (
                          <Chip size="small" label="External" color="primary" sx={{ mr: 1 }} />
                        )}
                        {agent.is_plugnplay && (
                          <Chip size="small" label="Plug & Play" color="secondary" sx={{ mr: 1 }} />
                        )}
                      {agent.billable_user_id && (
                        <Tooltip title={getBillableUserName(agent) || 'Billable user assigned'}>
                          <Chip 
                            icon={<AccountBalanceWalletIcon />} 
                            label="Billable" 
                            size="small" 
                            color="info"
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Created: {agent.created_at ? format(new Date(agent.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ 
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: 0.5,
                    p: 1,
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      overflowX: 'auto',
                      minHeight: '50px'
                  }}>
                      {/* Only show edit button for internal agents or if user has admin/agency permissions */}
                      {shouldShowEditButton(agent) && (
                      <Button 
                        size="small" 
                        onClick={(e) => handleEditAgent(agent.id, e)}
                        startIcon={<EditIcon />}
                          sx={{ whiteSpace: 'nowrap', minWidth: isMobile ? '40px' : '64px' }}
                      >
                        {isMobile ? '' : 'Edit'}
                      </Button>
                    )}
                    <Button 
                      size="small" 
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                          handleTestAgent(agent);
                      }}
                      startIcon={<VideocamIcon />}
                        sx={{ whiteSpace: 'nowrap', minWidth: isMobile ? '40px' : '64px' }}
                    >
                      {isMobile ? '' : 'Test'}
                    </Button>
                    {isAdmin && (
                      <Button 
                        size="small" 
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenBillingDialog(agent);
                        }}
                        startIcon={<AccountBalanceWalletIcon />}
                          sx={{ whiteSpace: 'nowrap', minWidth: isMobile ? '40px' : '64px' }}
                      >
                        {isMobile ? '' : 'Billing'}
                      </Button>
                    )}
                      {isAdmin && (
                      <Button 
                        size="small" 
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenClientConfigDialog(agent);
                        }}
                        startIcon={<SettingsIcon />}
                          sx={{ whiteSpace: 'nowrap', minWidth: isMobile ? '40px' : '64px' }}
                      >
                        {isMobile ? '' : 'Access'}
                      </Button>
                    )}
                      {/* Only admins can delete agents */}
                      {isAdmin && (
                    <Button 
                      size="small" 
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAgent(agent.id);
                      }}
                      startIcon={<DeleteIcon />}
                          sx={{ whiteSpace: 'nowrap', minWidth: isMobile ? '40px' : '64px' }}
                    >
                      {isMobile ? '' : 'Delete'}
                    </Button>
                      )}
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      ) : (
        // Functions Tab
        <Box sx={{ mt: 3 }}>
          <FunctionsList />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingAgent ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Agent Name"
            type="text"
            fullWidth
            value={newAgentName}
            onChange={(e) => setNewAgentName(e.target.value)}
          />
          
          {/* Calendar Settings */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Calendar Functionality</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={enableCalendar}
                  onChange={(e) => {
                    setEnableCalendar(e.target.checked);
                    if (e.target.checked && availableCalendars.length === 0) {
                      fetchAvailableCalendars();
                    }
                  }}
                />
              }
              label="Enable Calendar Functionality"
            />
            
            {enableCalendar && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" paragraph color="text.secondary">
                  When calendar functionality is enabled, the agent will be able to:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Box component="li">
                    <Typography variant="body2" color="text.secondary">
                      Check availability in your Google Calendar
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2" color="text.secondary">
                      Suggest available time slots to users
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body2" color="text.secondary">
                      Handle scheduling requests during conversations
                    </Typography>
                  </Box>
                </Box>
                
                <FormControl fullWidth margin="normal" disabled={loadingCalendars}>
                  <InputLabel>Select Calendar</InputLabel>
                  <Select
                    value={selectedCalendarId}
                    onChange={(e) => setSelectedCalendarId(e.target.value)}
                    label="Select Calendar"
                  >
                    {loadingCalendars ? (
                      <MenuItem value="" disabled>Loading calendars...</MenuItem>
                    ) : availableCalendars.length > 0 ? (
                      availableCalendars.map(calendar => (
                        <MenuItem key={calendar.id} value={calendar.id}>
                          {calendar.summary}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>No calendars available</MenuItem>
                    )}
                  </Select>
                  <FormHelperText>
                    {calendarError ? (
                      <span style={{ color: 'red' }}>{calendarError}</span>
                    ) : (
                      "Select which calendar the agent should use"
                    )}
                  </FormHelperText>
                </FormControl>
                
                {availableCalendars.length === 0 && !loadingCalendars && (
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 1 }}
                    onClick={() => {
                      handleCloseDialog();
                      navigate('/settings');
                    }}
                  >
                    Connect Google Calendar
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Client Configuration Permission */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Client Access Control</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={allowClientConfig}
                  onChange={(e) => setAllowClientConfig(e.target.checked)}
                />
              }
              label="Allow clients to configure this agent"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, clients and agencies this agent is assigned to can modify its settings.
              When disabled, they can use the agent but cannot change its configuration.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={editingAgent ? handleUpdateAgent : handleCreateAgent}
            color="primary"
            disabled={saving}
            startIcon={saving && <CircularProgress size={20} />}
          >
            {editingAgent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Billing Settings Dialog */}
      <Dialog 
        open={billingDialogOpen} 
        onClose={handleCloseBillingDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Change Billing Account
        </DialogTitle>
        <DialogContent>
          {loadingBillableUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                Select the user who will be billed for usage of {selectedAgent?.name}
              </Typography>
              
              {billableUsers.length === 0 ? (
                <Alert severity="warning" sx={{ my: 2 }}>
                  No users found who have access to this agent. Make sure to:
                  <ul>
                    <li>Assign users to this agent through the User Management section</li>
                    <li>Ensure the agent is assigned to at least one client organization</li>
                  </ul>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ my: 2 }}>
                  This list shows users who have this agent assigned to them ({billableUsers.length} found).
                </Alert>
              )}
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Billable Account</InputLabel>
                <Select
                  value={selectedBillableUserId}
                  onChange={(e) => setSelectedBillableUserId(e.target.value)}
                  label="Billable Account"
                >
                  <MenuItem value="">
                    <em>Default (Agent Owner)</em>
                  </MenuItem>
                  {billableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.email} - Balance: €{user.balance || '0.00'}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  The selected user's balance will be charged for all usage of this agent.
                  {!selectedBillableUserId && (
                    <Box component="span" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                      If no user is selected, the cost will be charged to the agent owner or according to organization hierarchy.
                    </Box>
                  )}
                </FormHelperText>
              </FormControl>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Cost: €0.15 per minute of usage
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBillingDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveBillingSettings}
            variant="contained"
            disabled={loadingBillableUsers}
          >
            Save Billing Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Configuration Dialog */}
      <Dialog 
        open={clientConfigDialogOpen} 
        onClose={handleCloseClientConfigDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Client Access Control
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Configure access permissions for {selectedAgent?.name}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            As the agent owner, you control whether clients can modify this agent's configuration.
          </Alert>
          
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={clientConfigurable}
                  onChange={(e) => setClientConfigurable(e.target.checked)}
                  disabled={savingClientConfig}
                />
              }
              label="Allow clients to configure this agent"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, clients and agencies this agent is assigned to can modify its settings.
              When disabled, they can use the agent but cannot change its configuration.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClientConfigDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveClientConfigSettings}
            variant="contained"
            disabled={savingClientConfig}
            startIcon={savingClientConfig && <CircularProgress size={20} />}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={callsDialog} 
        onClose={handleCloseCallsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedAgent ? `Calls for ${selectedAgent.name}` : 'Agent Calls'}
        </DialogTitle>
        <DialogContent>
          {loadingCalls ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : agentCalls.length === 0 ? (
            <Typography>No calls found for this agent</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Caller</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentCalls.map((call) => (
                    <TableRow key={call.id || `call-${Math.random()}`}>
                      <TableCell>{call.created_at ? format(new Date(call.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}</TableCell>
                      <TableCell>{call.caller || 'Unknown'}</TableCell>
                      <TableCell>{call.type || 'Call'}</TableCell>
                      <TableCell>{call.topic || 'No topic'}</TableCell>
                      <TableCell>{call.duration ? Math.round(call.duration) + 's' : 'N/A'}</TableCell>
                      <TableCell>€{call.cost ? Number(call.cost).toFixed(2) : '0.00'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCallsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Render the appropriate test component based on agent type */}
      {selectedTestAgent && selectedTestAgent.agent_type === 'outbound' ? (
        <LiveKitOutboundTester
          agent={selectedTestAgent}
          open={testAgentDialogOpen}
          onClose={handleCloseTestDialog}
        />
      ) : selectedTestAgent && (
        <Alert severity="info" sx={{ m: 2 }}>
          For plug-and-play agents, use the integrated test interface on the agent edit page.
        </Alert>
      )}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
  }
};

export default Agents; 